"use client";

import { Box, Columns, Row, Text, Tabs, Tab, TabPanel } from "@dxp-feed/ui";
import AddressContactDetails from "@/templates/storeDetail/AddressContactDetails";
import { IListStores } from "@/utils/redux/storeLocator";
import {
  getHolidaysWithDetails,
  isToday,
  formatStoreHours,
} from "@/helpers/storeLocatorHelper";
import { useEffect, useState } from "@/utils/hooks/reactUseCustomHooks";
import { IHoliday } from "@/utils/types/storeLocator";

interface IStoreInfoHours {
  store: IListStores | null;
}

const StoreInfoHours: React.FC<IStoreInfoHours> = ({ store }) => {
  const [holidaysWithDetails, setHolidaysWithDetails] = useState<IHoliday[]>(
    []
  );
  const daysOfWeek = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];
  const capitalizeFirstLetter = (str: string) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1);
  };

  useEffect(() => {
    if (store?.specialHours) {
      const holidays = getHolidaysWithDetails(store?.specialHours);
      setHolidaysWithDetails(holidays);
    }
  }, [store?.specialHours]);

  return (
    <Row className="lgs:py-12 py-6 6xl:gap-12 gap-0">
      <Columns className="col-7 6xl:w-[874px] lgs:w-7/12 w-full lgs:order-1 order-4 2md:pt-0 pt-6">
        <Text as="h2" className="font-sans text-2xl pb-6 font-extrabold">
          Information Hours
        </Text>
        <Tabs defaultActiveTab={0}>
          <Row>
            <Columns
              className={`col-4 6xl:w-[244px] lgs:w-4/12 w-full lgs:!pr-0 ${holidaysWithDetails.length < 1 ? "oneTab" : ""}`}
            >
              <div
                className="tab-list mb-[0] lgs:mb-[32px] lgs:block flex 
                  [&>[role=tab]]:lgs:!w-full [&>[role=tab]]:md:w-auto [&>[role=tab]]:w-1/2
                  [&>[role=tab]]:border-[#C4C4C4] 
                  [&>[role=tab]]:border-[1px] 
                  [&>[role=tab]]:lgs:!border-r-0 
                  [&>[role=tab]]:lgs:!border-b-[1px] [&>[role=tab]]:!border-b-0
                  [&>[role=tab]]:relative 
                  [&>[role=tab]]:!px-6 
                  [&>[role=tab]]:!py-3
                  [&>[role=tab]]:text-[14px]
                  [&>[role=tab]]:bg-[#F4F4F4]
                  [&>[role=tab]:first-child]:border-b-0 [&>[role=tab]:first-child]:border-r-0
                  [&>[aria-selected=true]]:!bg-brandWhite 
                  [&>[aria-selected=true]]:before:bg-[#003D2A] 
                  [&>[aria-selected=true]]:before:absolute 
                  [&>[aria-selected=true]]:before:left-[0px] 
                  [&>[aria-selected=true]]:before:shadow-[0px_0px_0px_1px_#003D2A]
                  [&>[aria-selected=true]]:before:lgs:top-0 [&>[aria-selected=true]]:before:bottom-0
                  [&>[aria-selected=true]]:before:content 
                  [&>[aria-selected=true]]:before:lgs:w-[2px] [&>[aria-selected=true]]:before:w-full 
                  [&>[aria-selected=true]]:before:lgs:h-full [&>[aria-selected=true]]:before:h-[2px] 
                  [&>[aria-selected=true]]:after:content 
                  [&>[aria-selected=true]]:after:lgs:bg-brandWhite [&>[aria-selected=true]]:after:bg-transparent 
                  [&>[aria-selected=true]]:after:absolute 
                  [&>[aria-selected=true]]:after:right-[-3px] 
                  [&>[aria-selected=true]]:after:w-[3px] 
                  [&>[aria-selected=true]]:after:h-full  
                  [&>[aria-selected=true]]:after:top-0  
                  [&>[aria-selected=true]]:!text-[#003D2A]"
                role="tablist"
                aria-label="Sample Tabs"
              >
                <Tab index={0} label="Store Hours" />
                {holidaysWithDetails && holidaysWithDetails.length > 0 && (
                  <Tab index={1} label="Holiday Hours" />
                )}
              </div>
            </Columns>
            <Columns className="col-8 6xl:w-[630px] lgs:w-8/12 w-full 6xl:!px-0 lgs:!pl-0">
              {/* <Box className="[&>[role=tab]]:text-[#003D2A] [&>[role=tab]]:font-bold [&>[role=tab]]:px-0 [&>[role=tab]]:py-0 [&>[role=tab]]:pb-3 [&>[aria-selected=true]]:bg-brandWhite [&>[aria-selected=true].icon-down-arrow:before]:content-['\e80f'] [&>[role=tab]]:border-b-[#C4C4C4]  [&_.icon-down-arrow]:before:absolute [&_.icon-down-arrow]:before:content-['\e850'] [&_.icon-down-arrow]:before:text-[9px]  [&_.icon-down-arrow]:before:right-[9px] [&_.icon-down-arrow]:before:top-[45%]">
                <Tab
                  index={0}
                  label="Store Hours"
                  className="lgs:hidden block icon-down-arrow relative"
                />
              </Box> */}
              <TabPanel index={0} className="transition-all duration-300">
                <Box
                  as="div"
                  className="w-full border-[1px] !px-4 border-[#C4C4C4]"
                >
                  <table
                    role="table"
                    className="w-full [&>tbody>tr:last-child]:border-none my-2"
                  >
                    <thead className="hidden">
                      <tr>
                        <th className="hidden">&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {store?.hours?.is24Hours ? (
                        <tr className="border-b border-[#C4C4C4]">
                          <th
                            colSpan={2}
                            className="py-[5px] text-body text-[14px] text-center font-bold"
                          >
                            Open 24 Hours
                          </th>
                        </tr>
                      ) : (
                        daysOfWeek.map((day) => (
                          <tr
                            key={day}
                            className={`border-b border-[#C4C4C4] ${isToday(day, store?.address?.province || "") ? "font-bold" : ""}`}
                          >
                            <th
                              className={`py-2 text-body text-[14px] text-left ${
                                isToday(day, store?.address?.province || "")
                                  ? "font-bold"
                                  : "font-normal"
                              }`}
                            >
                              {capitalizeFirstLetter(day.toLowerCase())}:
                            </th>
                            <td className="py-2 text-body text-[14px] text-right">
                              {store?.hours?.storeHours?.[day]
                                ? formatStoreHours(store.hours.storeHours[day])
                                : "Closed"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Box>
              </TabPanel>
              {/* <Box className="[&>[role=tab]]:text-[#003D2A] [&>[role=tab]]:font-bold [&>[role=tab]]:px-0 [&>[role=tab]]:py-0 [&>[role=tab]]:pb-3 [&>[aria-selected=true]]:bg-brandWhite [&>[aria-selected=true]]:!pt-3 [&>[aria-selected=true].icon-down-arrow:before]:content-['\e80f'] [&>[role=tab]]:border-b-[#C4C4C4]  [&_.icon-down-arrow]:before:absolute [&_.icon-down-arrow]:before:content-['\e850'] [&_.icon-down-arrow]:before:text-[9px] [&_.icon-down-arrow]:before:right-[9px] [&_.icon-down-arrow]:before:top-[45%]">
                {holidaysWithDetails && holidaysWithDetails.length > 0 && (
                  <Tab
                    index={1}
                    label="Holiday Hours"
                    className="lgs:hidden block icon-down-arrow relative"
                  />
                )} 
              </Box>*/}
              <TabPanel index={1} className="transition-all duration-300">
                <Box
                  as="div"
                  className="w-full border-[1px] !px-4 border-[#C4C4C4] lgs:min-h-[100px]"
                >
                  <table
                    role="table"
                    className="w-full [&>tbody>tr:last-child]:border-none my-2"
                  >
                    <thead className="hidden">
                      <tr>
                        <th className="hidden">&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holidaysWithDetails && holidaysWithDetails.length > 0 ? (
                        holidaysWithDetails.map((holiday, index) => {
                          return (
                            <tr
                              key={index}
                              className="lgs:border-b lgs:border-[#C4C4C4]"
                            >
                              <th className="py-2 text-body text-[14px] capitalize text-left">
                                {holiday.monthName}{" "}
                                {new Date(holiday.formattedDate).getUTCDate()} (
                                {holiday.holidayName}){" "}
                              </th>
                              <td className="py-2 text-body text-[14px] text-right whitespace-nowrap align-top">
                                {holiday?.isClosed
                                  ? "Closed"
                                  : `${holiday.openTime} to ${holiday.closeTime || "N/A"}`}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={2}
                            className="py-2 text-center text-body text-[14px]"
                          >
                            No holidays available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </TabPanel>
            </Columns>
          </Row>
        </Tabs>
      </Columns>
      {/* address component */}
      <AddressContactDetails store={store} />
    </Row>
  );
};

export default StoreInfoHours;
